/**
 * Continuous drive streaming state machine: sends `motion.velocity` while stick demand
 * is above epsilon, and emits exactly one `motion.stop` on the active → idle transition
 * (or when told to stop directly). This is the one policy in the module that spans many
 * ticks — everything else in commands.ts (posture/cargo/camera) is a single guarded send.
 */

import { TELEOP } from "./config";
import { clampVelocity } from "./motion";

import type { Dispatcher } from "./dispatcher";
import type { MotionProfile, SpeedAxis, VelocityDemand } from "../../types/commands";

function roundAxis(n: number): number {
  return Math.round(n * 1000) / 1000;
}

export class MotionStream {
  private streaming = false;

  constructor(
    private getSpeedLimits: () => Record<SpeedAxis, number>,
    private dispatcher: Dispatcher,
    /** Defaults to "holonomic" so tests/callers that never wire a robot-config-backed
     * profile (e.g. the default `getMotionProfile` in createTeleopCommands) keep
     * today's vx/vy/vyaw wire shape. */
    private getMotionProfile: () => MotionProfile = () => "holonomic"
  ) {}

  setVelocity(norm: VelocityDemand): boolean {
    const c = clampVelocity(norm ?? {});
    const profile = this.getMotionProfile();
    const active =
      profile === "ackermann"
        ? Math.abs(c.vx) > TELEOP.epsilon || Math.abs(c.steer_angle) > TELEOP.epsilon
        : Math.abs(c.vx) > TELEOP.epsilon ||
          Math.abs(c.vy) > TELEOP.epsilon ||
          Math.abs(c.vyaw) > TELEOP.epsilon;
    if (active) {
      const lim = this.getSpeedLimits();
      // A rover has no vy/vyaw at all — the wire payload only ever carries the axes
      // its own kinematics accept, not just zeroed-out placeholders for the rest.
      const payload =
        profile === "ackermann"
          ? {
              type: "motion.velocity",
              units: "si",
              vx: roundAxis(c.vx * lim.vx),
              steer_angle: roundAxis(c.steer_angle * lim.steer_angle),
            }
          : {
              type: "motion.velocity",
              units: "si",
              vx: roundAxis(c.vx * lim.vx),
              vy: roundAxis(c.vy * lim.vy),
              vyaw: roundAxis(c.vyaw * lim.vyaw),
            };
      const ok = this.dispatcher.dispatchSeq({ event: "send-velocity", payload });
      this.streaming = ok;
      return ok;
    }
    if (this.streaming) this.stop();
    return false;
  }

  stop(): boolean {
    const ok = this.dispatcher.dispatchSeq({
      event: "send-stop",
      payload: { type: "motion.stop" },
    });
    this.streaming = false;
    return ok;
  }

  isStreaming(): boolean {
    return this.streaming;
  }
}
